import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Search, FileText, Clock, CheckCircle, XCircle, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { trackingAPI } from '../lib/api';
import { useTheme } from '../App';

const STATUS_ICONS = { pending_payment: Clock, submitted: FileText, in_review: Clock, approved: CheckCircle, rejected: XCircle, completed: CheckCircle };

export default function TrackingPage() {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const [code, setCode] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [notFound, setNotFound] = useState(false);

  const handleTrack = async () => {
    if (!code.trim()) return;
    setLoading(true); setNotFound(false);
    try { const r = await trackingAPI.track(code.trim().toUpperCase()); setResult(r.data); } catch { setNotFound(true); setResult(null); } finally { setLoading(false); }
  };

  const StatusIcon = result ? STATUS_ICONS[result.status] || Clock : Clock;
  const statusSteps = ['pending_payment', 'submitted', 'in_review', 'approved'];
  const idx = result ? statusSteps.indexOf(result.status) : -1;
  const textP = isDark ? 'text-white' : 'text-gray-900';
  const textM = isDark ? 'text-white/40' : 'text-gray-400';
  const card = isDark ? 'bg-[#0A0A0C]/60 backdrop-blur-xl border border-white/[0.06]' : 'bg-white border border-gray-200 shadow-xl shadow-gray-200/50';
  const subCard = isDark ? 'bg-white/[0.02]' : 'bg-gray-50';
  const getStatusCl = (s) => {
    const m = { pending_payment: 'text-yellow-500 bg-yellow-500/10', submitted: 'text-blue-500 bg-blue-500/10', in_review: 'text-purple-500 bg-purple-500/10', approved: 'text-emerald-500 bg-emerald-500/10', rejected: 'text-red-500 bg-red-500/10', completed: 'text-cyan-500 bg-cyan-500/10' };
    return m[s] || '';
  };

  return (
    <div className={`min-h-screen flex items-center justify-center px-6 transition-colors ${isDark ? '' : 'bg-[#F8FAFC]'}`} data-testid="tracking-page">
      <div className="absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full pointer-events-none"
        style={{ background: isDark ? 'radial-gradient(circle, rgba(59,130,246,0.06) 0%, transparent 70%)' : 'radial-gradient(circle, rgba(59,130,246,0.03) 0%, transparent 70%)' }} />
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-lg relative">
        <div className="text-center mb-8">
          <div className={`inline-flex items-center justify-center w-14 h-14 rounded-2xl mb-4 ${isDark ? 'bg-blue-500/10 border border-blue-500/20' : 'bg-blue-50 border border-blue-200'}`}><Search className="w-7 h-7 text-blue-500" /></div>
          <h1 className={`text-3xl font-bold tracking-tight ${textP}`} style={{ fontFamily: 'Outfit' }}>{t('tracking.title')}</h1>
          <p className={`mt-2 text-sm ${textM}`}>{t('tracking.enterCode')}</p>
        </div>
        <div className="flex gap-2 mb-8">
          <Input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} onKeyDown={(e) => e.key === 'Enter' && handleTrack()}
            className={`h-14 text-center text-2xl tracking-[0.3em] font-mono rounded-xl ${isDark ? 'bg-white/[0.03] border-white/[0.06] text-white' : 'bg-gray-50 border-gray-200 text-gray-900'}`} placeholder="XXXXXXXX" maxLength={8} data-testid="tracking-code-input" />
          <Button onClick={handleTrack} disabled={loading || !code.trim()} className="h-14 px-6 bg-blue-500 hover:bg-blue-600 text-white rounded-xl" data-testid="track-btn">{loading ? '...' : <ArrowRight className="w-5 h-5" />}</Button>
        </div>

        {notFound && (<motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className={`text-center p-6 rounded-2xl ${isDark ? 'bg-red-500/5 border border-red-500/10' : 'bg-red-50 border border-red-200'}`}><XCircle className="w-8 h-8 text-red-500 mx-auto mb-2" /><p className="text-red-500">Appeal not found.</p></motion.div>)}

        {result && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className={`${card} rounded-2xl p-6 space-y-6`} data-testid="tracking-result">
            <div className="flex items-center justify-between">
              <div><p className={`text-xs uppercase tracking-wider ${textM}`}>Tracking Code</p><p className={`text-xl font-mono font-bold mt-1 ${textP}`}>{result.tracking_code}</p></div>
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${getStatusCl(result.status)}`}><StatusIcon className="w-6 h-6" /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {[['Platform', result.platform], ['Issue Type', result.issue_type], [t('tracking.created'), new Date(result.created_at).toLocaleDateString()], [t('tracking.updated'), new Date(result.updated_at).toLocaleDateString()]].map(([l,v]) => (
                <div key={l} className={`p-3 rounded-xl ${subCard}`}><p className={`text-xs ${textM}`}>{l}</p><p className={`text-sm mt-1 ${textP}`}>{v}</p></div>
              ))}
            </div>
            <div><p className={`text-xs uppercase tracking-wider mb-4 ${textM}`}>{t('tracking.status')} Progress</p>
              <div className="flex items-center gap-1">{statusSteps.map((s, i) => (<div key={s} className="flex-1"><div className={`w-full h-2 rounded-full transition-all ${i <= idx ? (result.status === 'rejected' && i === idx ? 'bg-red-500' : 'bg-blue-500') : isDark ? 'bg-white/[0.06]' : 'bg-gray-200'}`} /></div>))}</div>
              <div className="flex justify-between mt-2">{statusSteps.map(s => (<span key={s} className={`text-[10px] capitalize ${textM}`}>{s.replace('_',' ')}</span>))}</div>
            </div>
            <div className={`p-4 rounded-xl ${getStatusCl(result.status)}`}><p className="text-sm font-medium capitalize">Current Status: {result.status?.replace('_',' ')}</p></div>
          </motion.div>
        )}
      </motion.div>
    </div>
  );
}
